import * as React from "react";
import { BookOpen, CheckCircle, Table2 } from "lucide-react";
import clsx from "clsx";
import * as analytics from "@/services/analytics";

import { type Highlight, type SpreadsheetTarget } from "@/types/evidences";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { CitationInfo } from "@/modules/chat/chat-ui.types";
import { formatPageNumbers, highlightToHighlightInfo } from "@/modules/chat/citation.utils";

interface Props {
  citation: number | string;
  highlights: Highlight[];
  fseId: string;
  documentId: string;
  index: number;
  onCitationClick?: () => void;
  spreadsheetTarget?: SpreadsheetTarget;
  sourceDocumentContentType?: string | null;
}

const isSpreadsheetContentType = (contentType?: string | null): boolean => {
  const normalized = contentType?.toLowerCase() ?? "";
  return normalized.includes("spreadsheet") || normalized.includes("excel");
};

export const CitationButton: React.FC<Props> = ({
  citation,
  highlights,
  fseId,
  documentId,
  index,
  onCitationClick,
  spreadsheetTarget,
  sourceDocumentContentType,
}) => {
  const { handleCitationClick, currentCitation } = useCurrentPdf();

  const citationId = `${fseId}-${documentId}-${citation}-${index}`;

  const isActive = currentCitation?.citationId === citationId;
  const isSpreadsheetCitation = !!spreadsheetTarget || isSpreadsheetContentType(sourceDocumentContentType);

  const handleClick = React.useCallback(() => {
    analytics.evidenceShow();

    const firstHighlight = highlights[0];
    const allHighlightInfos = highlights.map(highlightToHighlightInfo);

    const citationInfo: CitationInfo = {
      citationId,
      highlights: allHighlightInfos,
      activeHighlightIndex: 0,
      fseId,
      documentId,
      citation,
      index,
      spreadsheetTarget,
      sourceDocumentContentType,
    };
    if (firstHighlight) {
      citationInfo.page = firstHighlight.page;
      citationInfo.boundingRect = {
        left: firstHighlight.min_x * 100,
        top: firstHighlight.min_y * 100,
        width: (firstHighlight.max_x - firstHighlight.min_x) * 100,
        height: (firstHighlight.max_y - firstHighlight.min_y) * 100,
      };
    }
    handleCitationClick(citationInfo);
    onCitationClick?.();
  }, [
    citationId,
    citation,
    documentId,
    fseId,
    handleCitationClick,
    highlights,
    index,
    onCitationClick,
    sourceDocumentContentType,
    spreadsheetTarget,
  ]);

  if (!highlights.length && !isSpreadsheetCitation) {
    return null;
  }

  const buttonClasses = clsx(
    "mx-0.5 inline-flex items-center justify-center rounded border border-solid px-1 py-0.5 text-xs font-medium transition-colors duration-200",
    {
      "border-brand-gold-500 bg-brand-gold-100 text-brand-gold-800 hover:bg-brand-gold-200": isActive,
      "border-midnight-blue-900 bg-[#585451] text-white hover:bg-midnight-blue-800": !isActive,
    },
  );

  const label = isSpreadsheetCitation ? spreadsheetTarget?.cellRef || "Sheet" : formatPageNumbers(highlights);

  return (
    <button type="button" onClick={handleClick} className={buttonClasses}>
      {isActive ? (
        <CheckCircle size={12} className="mr-0.5 text-brand-gold-800" />
      ) : isSpreadsheetCitation ? (
        <Table2 size={12} className="mr-0.5 text-white" />
      ) : (
        <BookOpen size={12} className="mr-0.5 text-white" />
      )}
      <span>{label}</span>
    </button>
  );
};
